import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Users,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Filter,
  FileText,
  Clock,
  Brain,
  List,
  HelpCircle,
  Home,
  ArrowLeft
} from 'lucide-react';

const AutonomyCaseMemory = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [memories, setMemories] = useState([]);
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  
  const [filters, setFilters] = useState({
    case_type: searchParams.get('case_type') || '',
    skip: parseInt(searchParams.get('skip')) || 0,
    limit: 20
  });

  const fetchMemories = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('skip', filters.skip);
      params.set('limit', filters.limit);
      if (filters.case_type) params.set('case_type', filters.case_type);
      
      const res = await api.get(`/admin/autonomy/case-memory?${params.toString()}`);
      setMemories(res.data.memories || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch case memories:', error);
      toast.error('Failed to load case memories');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);

  const handlePageChange = (direction) => {
    const newSkip = direction === 'next' 
      ? filters.skip + filters.limit 
      : Math.max(0, filters.skip - filters.limit);
    setFilters({ ...filters, skip: newSkip });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="autonomy-case-memory-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <Link to="/admin/autonomy" className="text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Case Memory</h1>
                <p className="text-sm text-gray-500">AI memory snapshots for cases</p>
              </div>
            </div>
            <Button onClick={fetchMemories} variant="outline" size="sm" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium">Filter:</span>
              <Input
                placeholder="Case Type..."
                value={filters.case_type}
                onChange={(e) => {
                  setFilters({ ...filters, case_type: e.target.value, skip: 0 });
                  if (e.target.value) {
                    setSearchParams({ case_type: e.target.value });
                  } else {
                    setSearchParams({});
                  }
                }}
                className="w-48"
                data-testid="filter-case-type"
              />
            </div>
          </CardContent>
        </Card>

        {/* Memories List */}
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Case Memories ({total})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : memories.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No case memories found</p>
              </div>
            ) : (
              <div className="divide-y">
                {memories.map((memory) => (
                  <div 
                    key={memory.id} 
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setExpandedId(expandedId === memory.id ? null : memory.id)}
                    data-testid={`memory-${memory.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-full bg-indigo-100">
                          <Brain className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-indigo-100 text-indigo-800">
                              {memory.case_type || 'Unknown Type'}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              {memory.snapshot_reason || 'Auto snapshot'}
                            </span>
                          </div>
                          <p className="text-sm mt-1 line-clamp-2">{memory.summary || 'No summary'}</p>
                          
                          {/* Entity References */}
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            {memory.case_id && (
                              <span className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                Case: {memory.case_id.slice(0, 8)}
                              </span>
                            )}
                            {memory.tenant_id && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                Tenant: {memory.tenant_id.slice(0, 8)}
                              </span>
                            )}
                            {memory.property_id && (
                              <span className="flex items-center gap-1">
                                <Home className="w-3 h-3" />
                                Property: {memory.property_id.slice(0, 8)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-400">
                        <p>{memory.snapshot_at ? new Date(memory.snapshot_at).toLocaleString() : '-'}</p>
                        {memory.created_by_agent && (
                          <p className="mt-1">by {memory.created_by_agent}</p>
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedId === memory.id && (
                      <div className="mt-4 pt-4 border-t space-y-4">
                        {/* Key Facts */}
                        {memory.key_facts && memory.key_facts.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                              <List className="w-3 h-3" />
                              Key Facts
                            </p>
                            <ul className="list-disc list-inside text-sm space-y-1">
                              {memory.key_facts.map((fact, idx) => (
                                <li key={idx} className="text-gray-700">{fact}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Timeline */}
                        {memory.timeline && memory.timeline.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Timeline
                            </p>
                            <div className="space-y-1">
                              {memory.timeline.slice(0, 5).map((event, idx) => (
                                <p key={idx} className="text-sm text-gray-600">
                                  {typeof event === 'string' ? event : JSON.stringify(event)}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Decisions Made */}
                        {memory.decisions_made && memory.decisions_made.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2">Decisions Made</p>
                            <div className="flex flex-wrap gap-2">
                              {memory.decisions_made.map((decision, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {typeof decision === 'string' ? decision : JSON.stringify(decision)}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Current Status & Next Action */}
                        <div className="grid grid-cols-2 gap-4">
                          {memory.current_status && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Current Status</p>
                              <p className="text-sm">{memory.current_status}</p>
                            </div>
                          )}
                          {memory.next_action_recommended && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Next Action</p>
                              <p className="text-sm text-blue-600">{memory.next_action_recommended}</p>
                            </div>
                          )}
                        </div>

                        {/* Outstanding Questions */}
                        {memory.outstanding_questions && memory.outstanding_questions.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                              <HelpCircle className="w-3 h-3" />
                              Outstanding Questions
                            </p>
                            <ul className="list-disc list-inside text-sm space-y-1 text-amber-700">
                              {memory.outstanding_questions.map((q, idx) => (
                                <li key={idx}>{q}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500">
            Showing {filters.skip + 1} - {Math.min(filters.skip + filters.limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.skip === 0}
              onClick={() => handlePageChange('prev')}
              data-testid="prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={filters.skip + filters.limit >= total}
              onClick={() => handlePageChange('next')}
              data-testid="next-page"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AutonomyCaseMemory;
